import { blinkCaret } from './blink-caret.module.css';

import { MessagePrimitive, useAuiState } from '@assistant-ui/react';
import clsx from 'clsx';
import { useCallback } from 'react';

import { Markdown } from '#components/markdown/markdown.js';
import { useSupersededMessageTextIds } from '../superseded-message-text-context.js';
import { AnnotatedToolCall } from './annotated-tool-call.js';
import { AssistantMessageActions } from './assistant-message-actions.js';
import { isHilDirectiveTableToolCall } from './hil-directive-tables.js';
import { HilDirectiveToolCall } from './hil-directive-tool-call.js';
import { ReasoningPart } from './reasoning-part.js';

import type { PartState } from '@assistant-ui/react';

/**
 * Renders an assistant message: logo gutter, text parts with streaming caret,
 * tool-call groups, reasoning blocks, and a hover-reveal action row.
 *
 * Renders nothing at all until at least one part has something to show, so a
 * message carrying only behavioural data parts never leaves a stranded logo
 * gutter above an empty body. Text parts of a message whose text has been
 * superseded are not shown.
 */
export function AssistantMessage() {
	const dataRenderers = useAuiState((s) => s.dataRenderers);
	const willPaintDataPart = useCallback(
		(name: string): boolean => (dataRenderers.renderers[name]?.[0] ?? dataRenderers.fallbacks[0]) !== undefined,
		[dataRenderers],
	);

	/**
	 * Coalesces runs of adjacent parts that read as one unit: every tool call, and
	 * consecutive data parts of the same kind, so a pipeline emitted as several
	 * successive widgets of one kind does not show gaps between its own rows.
	 * A part that paints nothing is left ungrouped — wrapping it would put an
	 * empty box in the body's flex flow, which reads as a blank line.
	 */
	const groupAdjacentParts = useCallback(
		(part: PartState): readonly [`group-${string}`] | null =>
			part.type === 'tool-call' ? (['group-tools'] as const)
			: part.type === 'data' && willPaintDataPart(part.name) ? ([`group-data-${part.name}`] as const)
			: null,
		[willPaintDataPart],
	);

	const supersededMessageIds = useSupersededMessageTextIds();
	const isTextSuperseded = useAuiState((s) => supersededMessageIds.has(s.message.id));

	const hasVisibleContent = useAuiState((s) =>
		s.message.content.some((part) =>
			part.type === 'data' ? willPaintDataPart(part.name)
			: part.type === 'text' ? !isTextSuperseded && part.text.length > 0
			: true,
		),
	);

	if (!hasVisibleContent) {
		return null;
	}

	return (
		<MessagePrimitive.Root className='group mb-4 grid grid-cols-[46px_1fr] items-start gap-x-2.5'>
			<div className='flex justify-end select-none'>
				<img src='/images/Cadstrom_DARK_PURPLE_LOGOMARK.png' alt='' className='h-4 w-4 object-contain' />
			</div>
			<div className='relative flex min-w-0 flex-col gap-4 text-sm leading-relaxed text-gray-a11'>
				<MessagePrimitive.GroupedParts groupBy={groupAdjacentParts}>
					{({ part, children }) => {
						// Only group keys start with `group-`; leaf parts fall through to the switch,
						// whose `children` is a sentinel that throws if rendered.
						if (part.type.startsWith('group-')) {
							// A group whose parts all render nothing must not survive as an empty
							// box: it would still take a slot in the body's flex gap and read as
							// a blank line. `empty:hidden` removes it as a flex item entirely.
							return <div className='space-y-1.5 empty:hidden'>{children}</div>;
						}
						switch (part.type) {
							case 'text': {
								if (isTextSuperseded) {
									return null;
								}
								return (
									<>
										<Markdown>{part.text}</Markdown>
										{part.status.type === 'running' && (
											<span className={clsx('inline-block h-3.5 w-1.5 bg-blue-9 align-middle', blinkCaret)} />
										)}
									</>
								);
							}
							case 'tool-call': {
								// Table-directive calls are presentation, not plumbing: render
								// the interactive table itself rather than a console row. A failed
								// call falls through to the console row so the error stays visible
								// and a retried call doesn't leave a duplicate table behind.
								if (isHilDirectiveTableToolCall(part.toolName) && part.isError !== true) {
									return <HilDirectiveToolCall toolName={part.toolName} args={part.args} />;
								}
								return (
									<AnnotatedToolCall
										toolCallId={part.toolCallId}
										toolName={part.toolName}
										args={part.args}
										result={part.result}
										isError={part.isError ?? false}
										status={part.status.type}
									/>
								);
							}
							case 'reasoning': {
								return <ReasoningPart text={part.text} status={part.status.type} />;
							}
							case 'data': {
								// Named data parts (e.g. the ingest-narration widgets) render
								// through the renderer registered for them via
								// `useAssistantDataUI`; unregistered ones render nothing.
								return part.dataRendererUI ?? null;
							}
							default: {
								return null;
							}
						}
					}}
				</MessagePrimitive.GroupedParts>
				<AssistantMessageActions />
			</div>
		</MessagePrimitive.Root>
	);
}
